import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { getWorkflowConfig, isCycleStep } from '../workflows/util'
import { ElementData, MetaEntry, WorkflowConfig, WorkflowStep } from '../lib/types'
import Loading from '../components/loading'
import { data as dataApi, tasks as taskApi } from '../lib/api'
import Form from '../components/form'
import MetaSummary from './meta-summary'
import { isComplete, scrollToTop } from '../lib/form-util'
import ErrorMsg from '../components/error-msg'
import advanceStep from './advance'
import getId from '../lib/get-id'
import { Button, Modal, ModalBody, TextArea } from '@carbon/react'
import WorkflowHistoryModal from '../components/modal/WorkflowHistoryModal'
import DuplicateRowsModal from '../components/modal/DuplicateRowsModal'
import { Add, Save } from '@carbon/icons-react'

export default function StepViewPage() {
    const { id, stepIndex, rowId, srcId } = useParams()
    const isNew = rowId === '_'
    const [config, setConfig] = useState<WorkflowConfig>()
    const [step, setStep] = useState<WorkflowStep>()
    const [data, setData] = useState<any>(null)
    const [meta, setMeta] = useState<MetaEntry | null>(null)
    const [update, setUpdate] = useState<any>(null)
    const [correction, setCorrection] = useState<any>(null)
    const [saving, setSaving] = useState(false)
    const [changes, setChanges] = useState<ElementData[]>([])
    const [editableRow, setEditableRow] = useState<null | number>(null);
    const [note, setNote] = useState('')
    const [sources, setSources] = useState('')
    const [err, setErr] = useState('')
    const [openErrModal, setOpenErrModal] = useState(false)
    const [workflowComplete, setWorkflowComplete] = useState(false)
    const [openNotesHistory, setOpenNotesHistory] = useState(false)
    const [loading, setLoading] = useState(true)
    const [isDuplicate, setIsDuplicate] = useState(false)
    const [duplicateRows, setDuplicateRows] = useState([])
    const navigate = useNavigate()
    useEffect(() => {
        setConfig(undefined)
        setStep(undefined)
        setData(null)
        setMeta(null)
        setUpdate(null)
        setCorrection(null)
        setSaving(false)
        setNote('')
        setErr('')
        setSources('')
        getWorkflowConfig(Number(id))
            .then((config) => {
                let step = config.steps[Number(stepIndex)]
                setConfig(config)
                setStep(step)
            })
            .catch((err) => setErr(err.message || err))

        if (isNew) {
            setData({})
        } else {
            dataApi
                .get(Number(id), Number(stepIndex), Number(rowId), Number(srcId))
                .then(({ row, meta, correction }) => {
                    setData(row)
                    setMeta(meta)
                    setCorrection(correction || null)
                    setNote(meta.notes || '')
                    setSources(meta.sources || '')
                })
                .catch((err) => setErr(err.message || err))
        }
        setLoading(false)
    }, [id, stepIndex, rowId, srcId, isNew])

    const backLink = useMemo(() => {
        if (!step) {
            return `/workflows/${id}/steps/${stepIndex}`
        } else {
            if (step.type === 'correct-cycle' || step.type === 'input-cycle' || step.skipTable) {
                return `/workflows/${id}`
            } else {
                return `/workflows/${id}/steps/${stepIndex}`
            }
        }
    }, [step, id, stepIndex])

    async function save() {
        setSaving(true)
        let method = isNew ? dataApi.insert : dataApi.save
        const bulkChanges = changes.length > 0
        // only save fields that have changed
        let saveData = isNew
            ? (bulkChanges ? changes : (update || data))
            : {
                id: getId(data),
                ...Object.fromEntries(
                    Object.entries(update).filter(([k]) => update[k] !== data[k]),
                ),
            }
        await method(Number(id), Number(stepIndex), Number(srcId), saveData, note || undefined, sources || undefined)
        if (step?.autoAdvance) navigate(advanceStep(config!, id!, stepIndex!, rowId!))
        else navigate(`/workflows/${id}`)
    }

    async function saveAndBack(overrideDuplicate: boolean) {
        setSaving(true)
        let method = isNew ? dataApi.insert : dataApi.save
        const bulkChanges = changes.length > 0
        const saveData = (bulkChanges ? changes : (update || data))
        await method(Number(id), Number(stepIndex), Number(srcId), saveData, note || undefined, sources || undefined, overrideDuplicate).then(() => onDiscard()).catch(
            (err) => {
                if (err.status && err.status === 409) {
                    setIsDuplicate(true)
                    setDuplicateRows(err.data)
                } else setErr(err.message || err)
            },
        )
        setSaving(false)

    }

    const onDiscard = () => {
        navigate(`/workflows/${id}`)
    }

    const onEdit = () => {
        //to filter out duplicate rows
        const filteredRows = changes.filter(change => {
            return duplicateRows.some(duplicate => {
                return step?.tableGrains?.every(key => {
                    return change[key] === duplicate[key];
                });
            });
        })
        if (filteredRows.length > 0) {
            let update = { ...filteredRows[0] }
            let note = String(update.note) || ''
            let sources = String(update.sources) || ''
            setChanges(filteredRows)
            setData(update)
            if (note) setNote(note)
            if (sources) setSources(sources)
        } else if (duplicateRows.length > 0) {
            setData(duplicateRows[0])
        } else {
            setData({})
        }
        setIsDuplicate(false)
        scrollToTop()
    }

    async function saveAndNext() {
        setSaving(true)
        let method = isNew ? dataApi.insert : dataApi.save
        await method(Number(id), Number(stepIndex), Number(srcId), update || data, note || undefined, sources)
            .catch((err) => setErr(err.message || err))
        setSaving(false)
        getNextTask()
    }

    async function getNextTask() {
        try {
            setOpenErrModal(false)
            setWorkflowComplete(false)
            const nextTask = await taskApi.getNext(Number(id))
            const srcId = nextTask.srcId || 0;
            navigate(`/workflows/${id}/steps/${stepIndex}/view/${nextTask.objId}/${srcId}`)
        } catch (err) {
            const message = (err as Error).message || String(err)
            if (message === 'Workflow cycle completed' || message === 'No correction tasks available') {
                setWorkflowComplete(true)
            } else {
                setOpenErrModal(true)
            }
        }
    }

    async function cancelCurrentTask() {
        try {
            setSaving(true)
            await taskApi.cancelTask(Number(id), Number(rowId))
            navigate(`/workflows/${id}`)
        } catch (err) {
            setErr((err as Error).message || String(err))
            setSaving(false)
            setOpenErrModal(true)
        }
       
    }

    function handleAddChanges() {
        if (Object.keys(update).length > 0) {
            update.note = note
            update.sources = sources
            setChanges(prev => [...prev, update])
            setData({})
            setUpdate(null)
            setNote('')
            setSources('')
            scrollToTop()
        }
    }

    function editRow(data: ElementData, index: number) {
        setData(data)
        setEditableRow && setEditableRow(index)
        data.note && setNote(String(data.note) || '')
        data.sources && setSources(String(data.sources) || '')

    }

    function deleteRow(index: number) {
        setChanges(prev => prev.filter((_, i) => i !== index))
    }

    function saveRow() {
        if (editableRow !== null) {
            const change = { ...update }
            change.note = note
            change.sources = sources
            setChanges(prev => prev.map((item, i) => i === editableRow ? change : item))
            setData({})
            setNote('')
            setSources('')
            setEditableRow(null)
            scrollToTop()
        }
    }

    const [complete, completeMsg] = useMemo(() => {
        if (!step || !data || changes.length) return [true, '']
        if (step && step.source?.required && sources === '')
            return [false, 'Field sources is required']
        return isComplete(step.view!, update || data,)
    }, [step, data, update, sources, changes])

    return (
        <div className='container mx-auto mt-10 pb-5'>
            <div className='mb-3'>
                <Link to={backLink}>
                    <i className='lni lni-chevron-left' /> Back to Data
                </Link>
            </div>
            <h1>{isNew ? 'New' : 'Edit'} Row</h1>
            {err && <ErrorMsg error={err} />}
            {config && meta && (
                <MetaSummary
                    meta={meta}
                    config={config}
                    onClickShowHistory={() => setOpenNotesHistory(true)}
                />
            )}

            {loading && <Loading />}
            {step && data && (
                <div>
                    <Form
                        isNew={isNew}
                        config={step.view!}
                        data={data}
                        destData={correction}
                        changes={changes}
                        onChange={setUpdate}
                        editRow={editRow}
                        deleteRow={deleteRow}
                    />
                    <hr className='my-5' />
                    <TextArea
                        labelText='Enter notes...'
                        helperText=''
                        rows={4}
                        id='notes-text-area'
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                    {step?.source && (
                        <TextArea
                            labelText={
                                <>
                                    Enter sources...
                                    {step.source.required ? (
                                        <span className='text-red-300 mx-1'>*</span>
                                    ) : (
                                        ''
                                    )}
                                </>
                            }
                            helperText='In case of multiple sources, separate them with comma.'
                            rows={2}
                            id='source-text-area'
                            value={sources}
                            required={step.source.required}
                            onChange={(e) => setSources(e.target.value)}
                            className='my-2'
                        />
                    )}
                    <div className='mt-3'>
                        {!isCycleStep(step) && (
                            <div>
                                {update || note || changes.length ? (
                                    <span className='flex flex-row items-center'>
                                        <Button
                                            size='sm'
                                            disabled={!complete || saving || editableRow !== null}
                                            onClick={
                                                step.skipTable ? () => saveAndBack(false) : save
                                            }
                                            renderIcon={Save}
                                        >
                                            {saving ? 'Saving...' : 'Save'}
                                        </Button>
                                        {isNew &&
                                            step.allowMultipleRows &&
                                            (editableRow !== null ? (
                                                <Button
                                                    size='sm'
                                                    className='ml-3'
                                                    renderIcon={Save}
                                                    onClick={saveRow}
                                                >
                                                    Save row
                                                </Button>
                                            ) : (
                                                <Button
                                                    size='sm'
                                                    className='ml-3'
                                                    disabled={!complete || saving}
                                                    renderIcon={Add}
                                                    onClick={handleAddChanges}
                                                >
                                                    Add new row
                                                </Button>
                                            ))}
                                        <Link className='button ml-3' to={`/workflows/${id}`}>
                                            Cancel
                                        </Link>
                                        <span className='text-yellow-500 ml-3 text-sm'>
                                            {completeMsg}
                                        </span>
                                    </span>
                                ) : (
                                    <span>
                                        {step.autoAdvance && (
                                            <Link
                                                className='button blue mr-3'
                                                to={advanceStep(config!, id!, stepIndex!, rowId!)}
                                            >
                                                Next
                                            </Link>
                                        )}
                                        <Link className='button' to={`/workflows/${id}`}>
                                            Cancel
                                        </Link>
                                        <span className='text-gray-500 ml-3 text-sm'>
                                            No edits made
                                        </span>
                                    </span>
                                )}
                            </div>
                        )}

                        {isCycleStep(step) && (
                            <div>
                                <button
                                    className='blue mr-2'
                                    disabled={!complete || saving}
                                    onClick={() => saveAndBack(false)}
                                >
                                    Save & Go Back
                                </button>
                                <button
                                    className='blue mr-2'
                                    disabled={!complete || saving}
                                    onClick={saveAndNext}
                                >
                                    Save & Next Task
                                </button>
                                <button className='mr-2' onClick={cancelCurrentTask}>
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <Modal
                danger
                open={openErrModal}
                modalHeading='Oops something went wrong!'
                onRequestSubmit={getNextTask}
                onSecondarySubmit={onDiscard}
                secondaryButtonText='Cancel'
                primaryButtonText='Try again'
            >
                <ModalBody>
                    {err && (
                        <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded'>
                            <strong>Error details:</strong>
                            <div className='mt-2 text-sm text-red-700'>{err}</div>
                        </div>
                    )}
                    Please click Try again to get a new task or Cancel to return to the Workflow
                    page
                </ModalBody>
            </Modal>
            <Modal
                open={workflowComplete}
                modalHeading='Workflow Complete!'
                onRequestSubmit={onDiscard}
                onSecondarySubmit={() => setWorkflowComplete(false)}
                secondaryButtonText='Close'
                primaryButtonText='Return to Workflow'
            >
                <ModalBody>
                    All tasks in this workflow have been completed. Click below to return to the
                    Workflow landing page.
                </ModalBody>
            </Modal>
            <WorkflowHistoryModal
                open={openNotesHistory}
                onClose={() => setOpenNotesHistory(false)}
                workflowId={Number(id)}
                rowId={Number(rowId)}
            />
            {step && (
                <DuplicateRowsModal
                    open={isDuplicate}
                    onClose={() => setIsDuplicate(false)}
                    data={duplicateRows}
                    config={step.view!}
                    onDiscard={onDiscard}
                    onEdit={onEdit}
                    onSubmit={saveAndBack}
                />
            )}
        </div>
    )
}
